import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Mail,
  MessageSquare,
  Target,
  Users,
  TrendingUp,
  Send,
  Clock,
  CheckCircle,
  MousePointer,
  DollarSign,
  RefreshCw,
  Play,
  BarChart3
} from 'lucide-react';
import CampaignsManagement from './CampaignsManagement';
import TemplatesManagement from './TemplatesManagement';
import MessageLogs from './MessageLogs';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function MarketingDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningAutomation, setRunningAutomation] = useState(false);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, schedulerRes] = await Promise.all([
        axiosInstance.get('/marketing/analytics'),
        axiosInstance.get('/marketing/scheduler-status')
      ]);
      setAnalytics(analyticsRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Failed to fetch marketing analytics:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const runAutomationsNow = async () => {
    setRunningAutomation(true);
    try {
      const response = await axiosInstance.post('/marketing/run-automations-now');
      toast.success(response.data.message);
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to run automations');
    } finally {
      setRunningAutomation(false);
    }
  };

  const statCards = analytics ? [
    {
      title: 'Total Campaigns',
      value: analytics.total_campaigns || 0,
      subtitle: `${analytics.active_campaigns || 0} active`,
      icon: Target,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Messages Sent',
      value: analytics.total_messages_sent || 0,
      subtitle: `${analytics.delivery_rate || 0}% delivered`,
      icon: Send,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Open Rate',
      value: `${analytics.open_rate || 0}%`,
      subtitle: `${analytics.total_opened || 0} opened`,
      icon: Mail,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      title: 'Click Rate',
      value: `${analytics.click_rate || 0}%`,
      subtitle: `${analytics.total_clicked || 0} clicks`,
      icon: MousePointer,
      color: 'text-orange-600',
      bg: 'bg-orange-50'
    },
    {
      title: 'Conversions',
      value: analytics.total_bookings || 0,
      subtitle: `${analytics.conversion_rate || 0}% rate`,
      icon: CheckCircle,
      color: 'text-[#595959]',
      bg: 'bg-gray-100'
    },
    {
      title: 'Revenue Generated',
      value: `$${(analytics.total_revenue || 0).toLocaleString()}`,
      subtitle: 'From campaigns',
      icon: DollarSign,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    }
  ] : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Automation</h1>
          <p className="text-gray-500">Manage campaigns, templates, and automations</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchAnalytics}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={runAutomationsNow}
            disabled={runningAutomation}
            className="bg-[#595959] hover:bg-[#404040]"
          >
            <Play className={`w-4 h-4 mr-2 ${runningAutomation ? 'animate-pulse' : ''}`} />
            Run Automations Now
          </Button>
        </div>
      </div>

      {/* Scheduler Status */}
      {schedulerStatus && (
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#595959]" />
                <div>
                  <span className="font-medium">Automation Scheduler: </span>
                  <Badge variant={schedulerStatus.running ? 'default' : 'destructive'}>
                    {schedulerStatus.running ? 'Running' : 'Stopped'}
                  </Badge>
                </div>
              </div>
              {schedulerStatus.jobs?.[0]?.next_run && (
                <p className="text-sm text-gray-500">
                  Next run: {formatDateTime(schedulerStatus.jobs[0].next_run)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Cards */}
      {!loading && analytics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.title}</p>
                <p className="text-xs text-gray-400 mt-1">{stat.subtitle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs for different sections */}
      <Tabs defaultValue="campaigns" className="space-y-4">
        <TabsList className="grid w-full max-w-lg grid-cols-4">
          <TabsTrigger value="campaigns" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Message Logs
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          <CampaignsManagement />
        </TabsContent>

        <TabsContent value="templates">
          <TemplatesManagement />
        </TabsContent>

        <TabsContent value="logs">
          <MessageLogs />
        </TabsContent>

        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Performance Analytics</CardTitle>
              <CardDescription>Detailed metrics and insights</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics ? (
                <div className="space-y-6">
                  {/* Funnel Visualization */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-700">Engagement Funnel</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-24 text-sm text-gray-600">Sent</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className="bg-blue-500 h-full rounded-full" style={{ width: '100%' }}></div>
                        </div>
                        <div className="w-16 text-sm font-medium">{analytics.total_messages_sent}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 text-sm text-gray-600">Delivered</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className="bg-green-500 h-full rounded-full" style={{ width: `${analytics.delivery_rate}%` }}></div>
                        </div>
                        <div className="w-16 text-sm font-medium">{analytics.total_delivered}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 text-sm text-gray-600">Opened</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className="bg-purple-500 h-full rounded-full" style={{ width: `${analytics.open_rate}%` }}></div>
                        </div>
                        <div className="w-16 text-sm font-medium">{analytics.total_opened}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 text-sm text-gray-600">Clicked</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className="bg-orange-500 h-full rounded-full" style={{ width: `${analytics.click_rate}%` }}></div>
                        </div>
                        <div className="w-16 text-sm font-medium">{analytics.total_clicked}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 text-sm text-gray-600">Converted</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div className="bg-gray-1000 h-full rounded-full" style={{ width: `${analytics.conversion_rate}%` }}></div>
                        </div>
                        <div className="w-16 text-sm font-medium">{analytics.total_bookings}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Loading analytics...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
